import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./App.css";
import "./i18n";
import { RTL_LANGS } from "./i18n";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Home from "./pages/Home";
import Plans from "./pages/Plans";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import EsimDetails from "./pages/EsimDetails";
import Topup from "./pages/Topup";
import PayFly from "./pages/PayFly";
import CalculatorPage from "./pages/CalculatorPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminEsims from "./pages/admin/AdminEsims";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminWebhooks from "./pages/admin/AdminWebhooks";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminGifts from "./pages/admin/AdminGifts";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminCrypto from "./pages/admin/AdminCrypto";
import AdminEmails from "./pages/admin/AdminEmails";
import TermsConditions from "./pages/legal/TermsConditions";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import RefundPolicy from "./pages/legal/RefundPolicy";
import AMLKYCPolicy from "./pages/legal/AMLKYCPolicy";
import WebsiteTerms from "./pages/legal/WebsiteTerms";
import CookieNotice from "./pages/legal/CookieNotice";
import USPrivacyNotice from "./pages/legal/USPrivacyNotice";
import DoNotSell from "./pages/legal/DoNotSell";
import LimitSensitive from "./pages/legal/LimitSensitive";
import PrivacyRequest from "./pages/legal/PrivacyRequest";
import CookieBanner from "./components/CookieBanner";
import { initTagLoader } from "./lib/tagLoader";

// Boot the consent-aware tag loader once. It's idempotent and reads
// REACT_APP_PLAUSIBLE_DOMAIN / REACT_APP_GA4_ID / REACT_APP_META_PIXEL_ID
// from the frontend env. With none set, it stays dormant.
if (typeof window !== "undefined") {
  initTagLoader();
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-12 text-ink-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  return children;
}

function HtmlAttrs() {
  const { i18n } = useTranslation();
  useEffect(() => {
    const isRTL = RTL_LANGS.includes(i18n.language);
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [i18n.language]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <HtmlAttrs />
        <Suspense fallback={<div className="p-12">…</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/calculator" element={<CalculatorPage />} />
            <Route path="/payfly" element={<Protected><PayFly /></Protected>} />
            <Route path="/checkout/:planId" element={<Checkout />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/esim/:id" element={<Protected><EsimDetails /></Protected>} />
            <Route path="/topup/:id" element={<Protected><Topup /></Protected>} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="esims" element={<AdminEsims />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="webhooks" element={<AdminWebhooks />} />
              <Route path="gifts" element={<AdminGifts />} />
              <Route path="crypto" element={<AdminCrypto />} />
              <Route path="emails" element={<AdminEmails />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            <Route path="/legal/terms" element={<TermsConditions />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/refunds" element={<RefundPolicy />} />
            <Route path="/legal/aml-kyc" element={<AMLKYCPolicy />} />
            <Route path="/legal/website-terms" element={<WebsiteTerms />} />
            <Route path="/legal/cookies" element={<CookieNotice />} />
            <Route path="/legal/us-privacy" element={<USPrivacyNotice />} />
            <Route path="/legal/do-not-sell" element={<DoNotSell />} />
            <Route path="/legal/limit-sensitive-pi" element={<LimitSensitive />} />
            <Route path="/legal/privacy-request" element={<PrivacyRequest />} />
            <Route path="/privacy-request" element={<Navigate to="/legal/privacy-request" replace />} />
            <Route path="/cookies" element={<Navigate to="/legal/cookies" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieBanner />
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
